"use client";

import Link from "next/link";
import { LazyImage } from "./lazy-image";
import type { Article } from "@/data/articles";

interface BlogSectionProps {
  title?: string;
  description?: string;
  articles: Article[];
}

export function BlogSection({
  title,
  description,
  articles,
}: BlogSectionProps) {
  return (
    <div className="container relative mx-auto">
      <div
        aria-hidden
        className="absolute inset-0 isolate contain-strict -z-10 opacity-60"
      >
        <div className="-rotate-45 bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,hsl(var(--foreground)/.06)_0,hsla(0,0%,55%,.02)_50%,hsl(var(--foreground)/.01)_80%)] absolute top-0 left-0 h-320 w-140 -translate-y-87.5 rounded-full" />
        <div className="-rotate-45 bg-[radial-gradient(50%_50%_at_50%_50%,hsl(var(--foreground)/.04)_0,hsl(var(--foreground)/.01)_80%,transparent_100%)] absolute top-0 left-0 h-320 w-60 [translate:5%_-50%] rounded-full" />
        <div className="-rotate-45 bg-[radial-gradient(50%_50%_at_50%_50%,hsl(var(--foreground)/.04)_0,hsl(var(--foreground)/.01)_80%,transparent_100%)] absolute top-0 left-0 h-320 w-60 -translate-y-87.5 rounded-full" />
      </div>
      <div className="absolute inset-x-0 w-full h-px" />
      <div className="z-10 grid px-4 md:grid-cols-2 lg:grid-cols-3">
        {articles.map((article) => (
          <Link
            href={article.href}
            key={article.id}
            className="flex flex-col gap-2 p-2 duration-75 rounded-lg group hover:bg-accent/60 active:bg-accent"
          >
            <LazyImage
              src={article.imageUrl}
              fallback="https://placehold.co/640x360?text=fallback-image"
              inView={true}
              alt={article.title}
              ratio={16 / 9}
              className="transition-all duration-500 group-hover:scale-105"
            />
            <div className="px-2 pb-2 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-[11px] sm:text-xs">
                <p>av {article.author}</p>
                <div className="rounded-full bg-muted-foreground size-1" />
                <p>{article.createdAt}</p>
                <div className="rounded-full bg-muted-foreground size-1" />
                <p>{article.readTime} min läsning</p>
              </div>
              <h3 className="text-lg font-semibold leading-5 tracking-tight line-clamp-2">
                {article.title}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-3">
                {article.description}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
